import React, { useState, useEffect, useRef, useMemo } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import html2canvas from 'html2canvas'
import Swal from 'sweetalert2'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const formatNumber = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatCurrency = (value) => `$${formatNumber(value)}`

const formatPercentage = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const Reports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [returnsRate, setReturnsRate] = useState(null)
  const [expenses, setExpenses] = useState([])

  // Chart refs
  const yearOverYearChartRef = useRef(null)
  const categoryRevenueChartRef = useRef(null)
  const topProductsChartRef = useRef(null)
  const branchRevenueChartRef = useRef(null)
  const warehouseRevenueChartRef = useRef(null)
  const stockTransferTypeChartRef = useRef(null)
  const stockTransferStatusChartRef = useRef(null)
  const stockTransferDailyChartRef = useRef(null)
  const topSourceWarehousesChartRef = useRef(null)
  const topDestinationBranchesChartRef = useRef(null)
  const reportsSectionRef = useRef(null)

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true)
      setError(null)
      try {
        const [analyticsRes, returnsRateRes, expensesRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.ANALYTICS_REPORTS),
          apiClient.get(API_ENDPOINTS.SALES_RETURNS_RATE).catch(err => {
            // If returns rate API fails, log but don't fail the whole report
            console.warn('Error fetching returns rate:', err)
            return { data: null }
          }),
          apiClient.get(API_ENDPOINTS.EXPENSES).catch(err => {
            // If expenses API fails, log but don't fail the whole report
            console.warn('Error fetching expenses:', err)
            return { data: [] }
          })
        ])
        setData(analyticsRes.data)
        setReturnsRate(returnsRateRes.data)
        const expensesData = Array.isArray(expensesRes.data) ? expensesRes.data : expensesRes.data?.results || []
        setExpenses(expensesData)
      } catch (err) {
        console.error('Error fetching reports:', err)
        setError('Failed to load reports. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchReports()
  }, [])

  // Initialize charts when data is available
  useEffect(() => {
    if (!data || !window.ApexCharts) return

    const destroyCharts = () => {
      const refs = [
        yearOverYearChartRef, categoryRevenueChartRef, topProductsChartRef,
        branchRevenueChartRef, warehouseRevenueChartRef, stockTransferTypeChartRef,
        stockTransferStatusChartRef, stockTransferDailyChartRef,
        topSourceWarehousesChartRef, topDestinationBranchesChartRef
      ]
      refs.forEach(ref => {
        if (ref.current?.chart) {
          ref.current.chart.destroy()
          ref.current.chart = null
        }
      })
    }

    destroyCharts()

    // Year Over Year Revenue - Column Chart
    if (data.year_over_year_revenue?.length && yearOverYearChartRef.current) {
      const chart = new window.ApexCharts(yearOverYearChartRef.current, {
        series: [
          {
            name: 'Current Year',
            data: data.year_over_year_revenue.map(item => {
              const val = Number(item.current_year ?? 0)
              return isNaN(val) ? 0 : val
            })
          },
          {
            name: 'Previous Year',
            data: data.year_over_year_revenue.map(item => {
              const val = Number(item.previous_year ?? 0)
              return isNaN(val) ? 0 : val
            })
          }
        ],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        xaxis: {
          categories: data.year_over_year_revenue.map(item => item.month_label)
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#ff771d'],
        fill: { opacity: 1 }
      })
      chart.render()
      yearOverYearChartRef.current.chart = chart
    }

    // Category Revenue Trend - Pie Chart
    if (data.category_revenue_trend?.length && categoryRevenueChartRef.current) {
      const chart = new window.ApexCharts(categoryRevenueChartRef.current, {
        series: data.category_revenue_trend.map(item => {
          const revenue = Number(item.revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'pie',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.category_revenue_trend.map(item => item.category_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7', '#3a0ca3', '#4361ee', '#4cc9f0']
      })
      chart.render()
      categoryRevenueChartRef.current.chart = chart
    }

    // Top Revenue Products - Bar Chart
    if (data.top_revenue_products?.length && topProductsChartRef.current) {
      const chart = new window.ApexCharts(topProductsChartRef.current, {
        series: [{
          name: 'Revenue',
          data: data.top_revenue_products.map(item => {
            const revenue = Number(item.revenue ?? 0)
            return isNaN(revenue) ? 0 : revenue
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.top_revenue_products.map(item => item.product_name)
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1']
      })
      chart.render()
      topProductsChartRef.current.chart = chart
    }

    // Branch Revenue Trend - Donut Chart
    if (data.branch_revenue_trend?.length && branchRevenueChartRef.current) {
      const chart = new window.ApexCharts(branchRevenueChartRef.current, {
        series: data.branch_revenue_trend.map(item => {
          const revenue = Number(item.revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.branch_revenue_trend.map(item => item.branch_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      branchRevenueChartRef.current.chart = chart
    }

    // Warehouse Revenue Trend - Donut Chart
    if (data.warehouse_revenue_trend?.length && warehouseRevenueChartRef.current) {
      const chart = new window.ApexCharts(warehouseRevenueChartRef.current, {
        series: data.warehouse_revenue_trend.map(item => {
          const revenue = Number(item.revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.warehouse_revenue_trend.map(item => item.warehouse_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#2eca6a', '#4154f1', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      warehouseRevenueChartRef.current.chart = chart
    }

    // Stock Transfer Statistics - By Type (Pie Chart)
    if (data.stock_transfer_statistics?.by_type?.length && stockTransferTypeChartRef.current) {
      const chart = new window.ApexCharts(stockTransferTypeChartRef.current, {
        series: data.stock_transfer_statistics.by_type.map(item => {
          const count = Number(item.count ?? 0)
          return isNaN(count) ? 0 : count
        }),
        chart: {
          type: 'pie',
          height: 400,
          toolbar: { show: true }
        },
        labels: data.stock_transfer_statistics.by_type.map(item => item.transfer_type_display),
        tooltip: {
          y: {
            formatter: (val) => `${val} transfers`
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: '0%'
            }
          }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center'
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d']
      })
      chart.render()
      stockTransferTypeChartRef.current.chart = chart
    }

    // Stock Transfer Statistics - By Status (Donut Chart)
    if (data.stock_transfer_statistics?.by_status?.length && stockTransferStatusChartRef.current) {
      const chart = new window.ApexCharts(stockTransferStatusChartRef.current, {
        series: data.stock_transfer_statistics.by_status.map(item => {
          const count = Number(item.count ?? 0)
          return isNaN(count) ? 0 : count
        }),
        chart: {
          type: 'donut',
          height: 400,
          toolbar: { show: true }
        },
        labels: data.stock_transfer_statistics.by_status.map(item => item.status_display),
        tooltip: {
          y: {
            formatter: (val) => `${val} transfers`
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: '60%'
            }
          }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center'
        },
        colors: ['#2eca6a', '#ffc107', '#dc3545', '#6c757d']
      })
      chart.render()
      stockTransferStatusChartRef.current.chart = chart
    }

    // Stock Transfer Daily Trend - Line Chart
    if (data.stock_transfer_statistics?.daily_trend?.length && stockTransferDailyChartRef.current) {
      const chart = new window.ApexCharts(stockTransferDailyChartRef.current, {
        series: [
          {
            name: 'Count',
            data: data.stock_transfer_statistics.daily_trend.map(item => {
              const count = Number(item.count ?? 0)
              return isNaN(count) ? 0 : count
            })
          },
          {
            name: 'Total Cost',
            data: data.stock_transfer_statistics.daily_trend.map(item => {
              const cost = Number(item.total_cost ?? 0)
              return isNaN(cost) ? 0 : cost
            })
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: data.stock_transfer_statistics.daily_trend.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Count' },
            labels: { formatter: (val) => Math.round(val) }
          },
          {
            opposite: true,
            title: { text: 'Cost' },
            labels: {
              formatter: (val) => formatCurrency(val)
            }
          }
        ],
        tooltip: {
          y: [
            { formatter: (val) => `${Math.round(val)} transfers` },
            { formatter: (val) => formatCurrency(val) }
          ]
        },
        colors: ['#4154f1', '#2eca6a'],
        stroke: { curve: 'smooth', width: 3 }
      })
      chart.render()
      stockTransferDailyChartRef.current.chart = chart
    }

    // Top Source Warehouses - Bar Chart
    if (data.stock_transfer_statistics?.top_source_warehouses?.length && topSourceWarehousesChartRef.current) {
      const chart = new window.ApexCharts(topSourceWarehousesChartRef.current, {
        series: [{
          name: 'Transfer Count',
          data: data.stock_transfer_statistics.top_source_warehouses.map(item => {
            const count = Number(item.transfer_count ?? 0)
            return isNaN(count) ? 0 : count
          })
        }],
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.stock_transfer_statistics.top_source_warehouses.map(item => item.warehouse_name)
        },
        colors: ['#4154f1']
      })
      chart.render()
      topSourceWarehousesChartRef.current.chart = chart
    }

    // Top Destination Branches - Bar Chart
    if (data.stock_transfer_statistics?.top_destination_branches?.length && topDestinationBranchesChartRef.current) {
      const chart = new window.ApexCharts(topDestinationBranchesChartRef.current, {
        series: [{
          name: 'Transfer Count',
          data: data.stock_transfer_statistics.top_destination_branches.map(item => {
            const count = Number(item.transfer_count ?? 0)
            return isNaN(count) ? 0 : count
          })
        }],
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.stock_transfer_statistics.top_destination_branches.map(item => item.branch_name)
        },
        colors: ['#2eca6a']
      })
      chart.render()
      topDestinationBranchesChartRef.current.chart = chart
    }

    return () => {
      destroyCharts()
    }
  }, [data])

  const topRevenueProducts = data?.top_revenue_products ?? []
  const branchRevenueTrend = data?.branch_revenue_trend ?? []
  const warehouseRevenueTrend = data?.warehouse_revenue_trend ?? []
  const reportCashiers = data?.cashier_performance?.cashiers ?? []
  const topReturnedProducts = returnsRate?.top_returned_products ?? []
  const topSourceWarehouses = data?.stock_transfer_statistics?.top_source_warehouses ?? []
  const topDestinationBranches = data?.stock_transfer_statistics?.top_destination_branches ?? []

  const expenseCategoryData = useMemo(() => {
    const categoryTotals = expenses.reduce((acc, expense) => {
      const category = expense.category_name || 'Uncategorized'
      acc[category] = (acc[category] || 0) + Number(expense.amount || 0)
      return acc
    }, {})
    return Object.entries(categoryTotals)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
  }, [expenses])

  const expenseBranchData = useMemo(() => {
    const branchTotals = expenses.reduce((acc, expense) => {
      const branch = expense.branch_name || 'No Branch'
      acc[branch] = (acc[branch] || 0) + Number(expense.amount || 0)
      return acc
    }, {})
    return Object.entries(branchTotals)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
  }, [expenses])

  const sortedExpenses = useMemo(
    () =>
      [...expenses].sort(
        (a, b) => new Date(b.incurred_on || b.created_at) - new Date(a.incurred_on || a.created_at)
      ),
    [expenses]
  )

  const {
    paginatedItems: paginatedTopProducts,
    currentPage: topProductsPage,
    setCurrentPage: setTopProductsPage,
    pageSize: topProductsPageSize,
    setPageSize: setTopProductsPageSize,
    totalItems: topProductsTotal,
    totalPages: topProductsTotalPages,
    startIndex: topProductsStart,
    endIndex: topProductsEnd
  } = usePagination(topRevenueProducts)

  const {
    paginatedItems: paginatedBranchRevenue,
    currentPage: branchRevenuePage,
    setCurrentPage: setBranchRevenuePage,
    pageSize: branchRevenuePageSize,
    setPageSize: setBranchRevenuePageSize,
    totalItems: branchRevenueTotal,
    totalPages: branchRevenueTotalPages,
    startIndex: branchRevenueStart,
    endIndex: branchRevenueEnd
  } = usePagination(branchRevenueTrend)

  const {
    paginatedItems: paginatedWarehouseRevenue,
    currentPage: warehouseRevenuePage,
    setCurrentPage: setWarehouseRevenuePage,
    pageSize: warehouseRevenuePageSize,
    setPageSize: setWarehouseRevenuePageSize,
    totalItems: warehouseRevenueTotal,
    totalPages: warehouseRevenueTotalPages,
    startIndex: warehouseRevenueStart,
    endIndex: warehouseRevenueEnd
  } = usePagination(warehouseRevenueTrend)

  const {
    paginatedItems: paginatedCashiers,
    currentPage: cashiersPage,
    setCurrentPage: setCashiersPage,
    pageSize: cashiersPageSize,
    setPageSize: setCashiersPageSize,
    totalItems: cashiersTotal,
    totalPages: cashiersTotalPages,
    startIndex: cashiersStart,
    endIndex: cashiersEnd
  } = usePagination(reportCashiers)

  const {
    paginatedItems: paginatedReturnedProducts,
    currentPage: returnedProductsPage,
    setCurrentPage: setReturnedProductsPage,
    pageSize: returnedProductsPageSize,
    setPageSize: setReturnedProductsPageSize,
    totalItems: returnedProductsTotal,
    totalPages: returnedProductsTotalPages,
    startIndex: returnedProductsStart,
    endIndex: returnedProductsEnd
  } = usePagination(topReturnedProducts)

  const {
    paginatedItems: paginatedSourceWarehouses,
    currentPage: sourceWarehousesPage,
    setCurrentPage: setSourceWarehousesPage,
    pageSize: sourceWarehousesPageSize,
    setPageSize: setSourceWarehousesPageSize,
    totalItems: sourceWarehousesTotal,
    totalPages: sourceWarehousesTotalPages,
    startIndex: sourceWarehousesStart,
    endIndex: sourceWarehousesEnd
  } = usePagination(topSourceWarehouses)

  const {
    paginatedItems: paginatedDestinationBranches,
    currentPage: destinationBranchesPage,
    setCurrentPage: setDestinationBranchesPage,
    pageSize: destinationBranchesPageSize,
    setPageSize: setDestinationBranchesPageSize,
    totalItems: destinationBranchesTotal,
    totalPages: destinationBranchesTotalPages,
    startIndex: destinationBranchesStart,
    endIndex: destinationBranchesEnd
  } = usePagination(topDestinationBranches)

  const {
    paginatedItems: paginatedExpenseCategories,
    currentPage: expenseCategoriesPage,
    setCurrentPage: setExpenseCategoriesPage,
    pageSize: expenseCategoriesPageSize,
    setPageSize: setExpenseCategoriesPageSize,
    totalItems: expenseCategoriesTotal,
    totalPages: expenseCategoriesTotalPages,
    startIndex: expenseCategoriesStart,
    endIndex: expenseCategoriesEnd
  } = usePagination(expenseCategoryData)

  const {
    paginatedItems: paginatedExpenseBranches,
    currentPage: expenseBranchesPage,
    setCurrentPage: setExpenseBranchesPage,
    pageSize: expenseBranchesPageSize,
    setPageSize: setExpenseBranchesPageSize,
    totalItems: expenseBranchesTotal,
    totalPages: expenseBranchesTotalPages,
    startIndex: expenseBranchesStart,
    endIndex: expenseBranchesEnd
  } = usePagination(expenseBranchData)

  const {
    paginatedItems: paginatedExpenses,
    currentPage: expensesPage,
    setCurrentPage: setExpensesPage,
    pageSize: expensesPageSize,
    setPageSize: setExpensesPageSize,
    totalItems: expensesTableTotal,
    totalPages: expensesTotalPages,
    startIndex: expensesStart,
    endIndex: expensesEnd
  } = usePagination(sortedExpenses)

  if (loading) {
    return (
      <>
        <div className="pagetitle">
          <h1>Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading reports...</span>
            </div>
          </div>
        </section>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className="pagetitle">
          <h1>Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </section>
      </>
    )
  }

  const stockTransferStats = data?.stock_transfer_statistics || {}
  const cashierPerformance = data?.cashier_performance || {}

  const generatePDF = async () => {
    if (!data) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No report data available to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    if (!reportsSectionRef.current) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to capture report section.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      // Show loading message
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we capture the report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      // Capture the reports section as canvas
      const canvas = await html2canvas(reportsSectionRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: reportsSectionRef.current.scrollWidth,
        windowHeight: reportsSectionRef.current.scrollHeight
      })

      const imgData = canvas.toDataURL('image/png')
      const imgWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight

      const doc = new jsPDF('portrait', 'mm', 'a4')
      let position = 0

      // Add first page
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      // Add additional pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        doc.addPage()
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      // Save PDF
      const fileName = `Analytics_Reports_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'The report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1>Analytics Reports</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">Reports</li>
              </ol>
            </nav>
          </div>
          {data && (
            <button
              onClick={generatePDF}
              className="btn btn-primary"
              style={{ height: 'fit-content' }}
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              Download PDF
            </button>
          )}
        </div>
      </div>

      <section className="section" ref={reportsSectionRef}>
        {/* Revenue Trends Section */}
        <div className="row">
          <div className="col-12">
            <h5 className="card-title mb-3">
              <i className="bi bi-graph-up-arrow text-primary me-2"></i>
              Revenue Trends
            </h5>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Daily Revenue Trend</h5>
                {data?.daily_revenue_trend?.length ? (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.daily_revenue_trend.map((item, idx) => {
                          const date = new Date(item.day)
                          return (
                            <tr key={idx}>
                              <td>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</td>
                              <td className="text-end fw-semibold">{formatCurrency(item.revenue)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">
                            {formatCurrency(data.daily_revenue_trend.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-calendar-x fs-3 mb-2 d-block"></i>
                      <span>No daily revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Weekly Revenue Trend</h5>
                {data?.weekly_revenue_trend?.length ? (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Week</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.weekly_revenue_trend.map((item, idx) => {
                          const date = new Date(item.week)
                          return (
                            <tr key={idx}>
                              <td>Week of {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                              <td className="text-end fw-semibold">{formatCurrency(item.revenue)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">
                            {formatCurrency(data.weekly_revenue_trend.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-calendar-week fs-3 mb-2 d-block"></i>
                      <span>No weekly revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Monthly Revenue Trend</h5>
                {data?.monthly_revenue_trend?.length ? (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Month</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.monthly_revenue_trend.map((item, idx) => {
                          const date = new Date(item.month)
                          return (
                            <tr key={idx}>
                              <td>{date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</td>
                              <td className="text-end fw-semibold">{formatCurrency(item.revenue)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">
                            {formatCurrency(data.monthly_revenue_trend.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-calendar-month fs-3 mb-2 d-block"></i>
                      <span>No monthly revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Year Over Year Revenue */}
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Year Over Year Revenue Comparison</h5>
                {data?.year_over_year_revenue?.length ? (
                  <div ref={yearOverYearChartRef}></div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-bar-chart fs-3 mb-2 d-block"></i>
                    <span>No year-over-year data available</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category and Product Revenue */}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Revenue by Category</h5>
                {data?.category_revenue_trend?.length ? (
                  <div ref={categoryRevenueChartRef} className="flex-grow-1"></div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No category revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Top Revenue Products</h5>
                {data?.top_revenue_products?.length ? (
                  <>
                    <div ref={topProductsChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <h6 className="mb-2">Product Details</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>SKU</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedTopProducts.map((product, idx) => (
                              <tr key={product.product_id || idx}>
                                <td>{product.product_name}</td>
                                <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                <td className="text-end fw-semibold">{formatCurrency(product.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <TablePagination
                          currentPage={topProductsPage}
                          totalPages={topProductsTotalPages}
                          totalItems={topProductsTotal}
                          startIndex={topProductsStart}
                          endIndex={topProductsEnd}
                          pageSize={topProductsPageSize}
                          onPageChange={setTopProductsPage}
                          onPageSizeChange={setTopProductsPageSize}
                          itemLabel="products"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-bar-chart fs-3 mb-2 d-block"></i>
                      <span>No product revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Branch and Warehouse Revenue */}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Revenue by Branch</h5>
                {data?.branch_revenue_trend?.length ? (
                  <>
                    <div ref={branchRevenueChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Branch</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedBranchRevenue.map((branch, idx) => (
                              <tr key={branch.branch_id || idx}>
                                <td>{branch.branch_name}</td>
                                <td className="text-end fw-semibold">{formatCurrency(branch.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <TablePagination
                          currentPage={branchRevenuePage}
                          totalPages={branchRevenueTotalPages}
                          totalItems={branchRevenueTotal}
                          startIndex={branchRevenueStart}
                          endIndex={branchRevenueEnd}
                          pageSize={branchRevenuePageSize}
                          onPageChange={setBranchRevenuePage}
                          onPageSizeChange={setBranchRevenuePageSize}
                          itemLabel="branches"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No branch revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Revenue by Warehouse</h5>
                {data?.warehouse_revenue_trend?.length ? (
                  <>
                    <div ref={warehouseRevenueChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Warehouse</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedWarehouseRevenue.map((warehouse, idx) => (
                              <tr key={warehouse.warehouse_id || idx}>
                                <td>{warehouse.warehouse_name}</td>
                                <td className="text-end fw-semibold">{formatCurrency(warehouse.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <TablePagination
                          currentPage={warehouseRevenuePage}
                          totalPages={warehouseRevenueTotalPages}
                          totalItems={warehouseRevenueTotal}
                          startIndex={warehouseRevenueStart}
                          endIndex={warehouseRevenueEnd}
                          pageSize={warehouseRevenuePageSize}
                          onPageChange={setWarehouseRevenuePage}
                          onPageSizeChange={setWarehouseRevenuePageSize}
                          itemLabel="warehouses"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No warehouse revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cashier Performance */}
        {cashierPerformance.cashiers?.length > 0 && (
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Cashier Performance</h5>
                    <div className="text-muted small">
                      Range: {cashierPerformance.selected_range && (
                        <>
                          {new Date(cashierPerformance.range_start).toLocaleDateString()} - {new Date(cashierPerformance.range_end).toLocaleDateString()}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Cashier</th>
                          <th>Email</th>
                          <th className="text-end">Total Sales Amount</th>
                          <th className="text-end">Transaction Count</th>
                          <th className="text-end">Avg Sale Value</th>
                          <th className="text-end">Items Sold</th>
                          <th className="text-end">Refunds</th>
                          <th className="text-end">Voids</th>
                          <th className="text-end">Refund Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCashiers.map((cashier, idx) => (
                          <tr key={cashier.cashier_id || idx}>
                            <td className="fw-semibold">{cashier.cashier_name}</td>
                            <td><small className="text-muted">{cashier.cashier_email}</small></td>
                            <td className="text-end">{formatCurrency(cashier.total_sales_amount)}</td>
                            <td className="text-end">{formatNumber(cashier.transaction_count)}</td>
                            <td className="text-end">{formatCurrency(cashier.average_sale_value)}</td>
                            <td className="text-end">{formatNumber(cashier.total_items_sold)}</td>
                            <td className="text-end">
                              <span className="badge bg-warning">{cashier.refund_count}</span>
                            </td>
                            <td className="text-end">
                              <span className="badge bg-danger">{cashier.void_count}</span>
                            </td>
                            <td className="text-end">
                              <span className={`badge ${cashier.refund_rate > 5 ? 'bg-danger' : cashier.refund_rate > 2 ? 'bg-warning' : 'bg-success'}`}>
                                {cashier.refund_rate.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={cashiersPage}
                      totalPages={cashiersTotalPages}
                      totalItems={cashiersTotal}
                      startIndex={cashiersStart}
                      endIndex={cashiersEnd}
                      pageSize={cashiersPageSize}
                      onPageChange={setCashiersPage}
                      onPageSizeChange={setCashiersPageSize}
                      itemLabel="cashiers"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Returns Rate Section */}
        {returnsRate && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-counterclockwise text-primary me-2"></i>
                  Returns Rate Analysis
                </h5>
              </div>
            </div>

            {/* Returns Rate Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sold Quantity</h6>
                    <h4 className="mb-0 text-primary">{formatNumber(returnsRate.metrics?.total_sold_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sales Value</h6>
                    <h4 className="mb-0 text-success">{formatCurrency(returnsRate.metrics?.total_sales_value || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Returned Quantity</h6>
                    <h4 className="mb-0 text-danger">{formatNumber(returnsRate.metrics?.total_returned_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Refund Amount</h6>
                    <h4 className="mb-0 text-warning">{formatCurrency(returnsRate.metrics?.total_refund_amount || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Return Rate Metrics */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Quantity Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.quantity_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {returnsRate.metrics?.total_returned_quantity || 0} of {returnsRate.metrics?.total_sold_quantity || 0} items returned
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Value Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.value_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {formatCurrency(returnsRate.metrics?.total_refund_amount || 0)} of {formatCurrency(returnsRate.metrics?.total_sales_value || 0)} refunded
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Returned Products */}
            {returnsRate.top_returned_products?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Top Returned Products</h5>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th className="text-end">Returned Quantity</th>
                                <th className="text-end">Refund Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedReturnedProducts.map((product, idx) => (
                                <tr key={product.product_id || idx}>
                                  <td className="fw-semibold">{product.product_name}</td>
                                  <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                  <td className="text-end text-danger fw-semibold">{formatNumber(product.total_returned_quantity)}</td>
                                  <td className="text-end text-warning fw-semibold">{formatCurrency(product.total_refund_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <TablePagination
                            currentPage={returnedProductsPage}
                            totalPages={returnedProductsTotalPages}
                            totalItems={returnedProductsTotal}
                            startIndex={returnedProductsStart}
                            endIndex={returnedProductsEnd}
                            pageSize={returnedProductsPageSize}
                            onPageChange={setReturnedProductsPage}
                            onPageSizeChange={setReturnedProductsPageSize}
                            itemLabel="products"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Information */}
            {returnsRate.filters && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="text-muted mb-2">Report Period</h6>
                      <p className="mb-0">
                        <strong>Start Date:</strong> {returnsRate.filters.start_date ? new Date(returnsRate.filters.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} &nbsp;|&nbsp;
                        <strong>End Date:</strong> {returnsRate.filters.end_date ? new Date(returnsRate.filters.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        {returnsRate.filters.branch_id && (
                          <> &nbsp;|&nbsp; <strong>Branch ID:</strong> {returnsRate.filters.branch_id}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Stock Transfer Statistics */}
        {stockTransferStats.summary && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-left-right text-primary me-2"></i>
                  Stock Transfer Statistics
                </h5>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Transfers</h6>
                    <h4 className="mb-0 text-primary">{stockTransferStats.summary.total_transfers || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Completed</h6>
                    <h4 className="mb-0 text-success">{stockTransferStats.summary.completed_transfers || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Pending</h6>
                    <h4 className="mb-0 text-warning">{stockTransferStats.summary.pending_transfers || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Completion Rate</h6>
                    <h4 className="mb-0 text-info">{stockTransferStats.summary.completion_rate?.toFixed(1) || 0}%</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Transfer Charts */}
            <div className="row g-4 mb-4">
              <div className="col-lg-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">By Type</h5>
                    {stockTransferStats.by_type?.length ? (
                      <div ref={stockTransferTypeChartRef} className="flex-grow-1"></div>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                          <span>No data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">By Status</h5>
                    {stockTransferStats.by_status?.length ? (
                      <div ref={stockTransferStatusChartRef} className="flex-grow-1"></div>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                          <span>No data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Summary</h5>
                    <div className="mt-3 flex-grow-1 d-flex flex-column justify-content-center">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total Cost:</span>
                        <strong>{formatCurrency(stockTransferStats.summary.total_cost || 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total Quantity:</span>
                        <strong>{formatNumber(stockTransferStats.summary.total_quantity || 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>In Transit:</span>
                        <strong>{stockTransferStats.summary.in_transit_transfers || 0}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Cancelled:</span>
                        <strong>{stockTransferStats.summary.cancelled_transfers || 0}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Transfer Daily Trend */}
            {stockTransferStats.daily_trend?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Daily Transfer Trend</h5>
                      <div ref={stockTransferDailyChartRef}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Source Warehouses and Destination Branches */}
            <div className="row g-4 mb-4">
              {stockTransferStats.top_source_warehouses?.length > 0 && (
                <div className="col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">Top Source Warehouses</h5>
                      <div ref={topSourceWarehousesChartRef} className="flex-grow-1"></div>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Warehouse</th>
                                <th className="text-end">Transfers</th>
                                <th className="text-end">Total Cost</th>
                                <th className="text-end">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedSourceWarehouses.map((warehouse, idx) => (
                                <tr key={warehouse.warehouse_id || idx}>
                                  <td>{warehouse.warehouse_name}</td>
                                  <td className="text-end">{warehouse.transfer_count}</td>
                                  <td className="text-end">{formatCurrency(warehouse.total_cost)}</td>
                                  <td className="text-end">{formatNumber(warehouse.total_quantity)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <TablePagination
                            currentPage={sourceWarehousesPage}
                            totalPages={sourceWarehousesTotalPages}
                            totalItems={sourceWarehousesTotal}
                            startIndex={sourceWarehousesStart}
                            endIndex={sourceWarehousesEnd}
                            pageSize={sourceWarehousesPageSize}
                            onPageChange={setSourceWarehousesPage}
                            onPageSizeChange={setSourceWarehousesPageSize}
                            itemLabel="warehouses"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {stockTransferStats.top_destination_branches?.length > 0 && (
                <div className="col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">Top Destination Branches</h5>
                      <div ref={topDestinationBranchesChartRef} className="flex-grow-1"></div>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Branch</th>
                                <th className="text-end">Transfers</th>
                                <th className="text-end">Total Cost</th>
                                <th className="text-end">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedDestinationBranches.map((branch, idx) => (
                                <tr key={branch.branch_id || idx}>
                                  <td>{branch.branch_name}</td>
                                  <td className="text-end">{branch.transfer_count}</td>
                                  <td className="text-end">{formatCurrency(branch.total_cost)}</td>
                                  <td className="text-end">{formatNumber(branch.total_quantity)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <TablePagination
                            currentPage={destinationBranchesPage}
                            totalPages={destinationBranchesTotalPages}
                            totalItems={destinationBranchesTotal}
                            startIndex={destinationBranchesStart}
                            endIndex={destinationBranchesEnd}
                            pageSize={destinationBranchesPageSize}
                            onPageChange={setDestinationBranchesPage}
                            onPageSizeChange={setDestinationBranchesPageSize}
                            itemLabel="branches"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Expenses Section */}
        {expenses.length > 0 && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-cash-stack text-primary me-2"></i>
                  Expenses Report
                </h5>
              </div>
            </div>

            {/* Expenses Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Expenses</h6>
                    <h4 className="mb-0 text-danger">
                      {formatCurrency(expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Records</h6>
                    <h4 className="mb-0 text-primary">{expenses.length}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Average Expense</h6>
                    <h4 className="mb-0 text-info">
                      {formatCurrency(
                        expenses.length > 0
                          ? expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0) / expenses.length
                          : 0
                      )}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Unique Categories</h6>
                    <h4 className="mb-0 text-success">
                      {new Set(expenses.map((e) => e.category_name).filter(Boolean)).size}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses by Category */}
            {expenseCategoryData.length > 0 && (
                <div className="row g-4 mb-4">
                  <div className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-body">
                        <h5 className="card-title">Expenses by Category</h5>
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Category</th>
                                <th className="text-end">Total Amount</th>
                                <th className="text-end">Count</th>
                                <th className="text-end">Percentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedExpenseCategories.map((category, idx) => {
                                const count = expenses.filter((e) => (e.category_name || 'Uncategorized') === category.name).length
                                const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0)
                                const percentage = totalExpenses > 0 ? (category.total / totalExpenses) * 100 : 0
                                return (
                                  <tr key={idx}>
                                    <td className="fw-semibold">{category.name}</td>
                                    <td className="text-end fw-semibold">{formatCurrency(category.total)}</td>
                                    <td className="text-end">{count}</td>
                                    <td className="text-end">
                                      <span className="badge bg-info">{formatPercentage(percentage)}%</span>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                            <tfoot className="table-light">
                              <tr>
                                <td className="fw-bold">Total</td>
                                <td className="text-end fw-bold">
                                  {formatCurrency(expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}
                                </td>
                                <td className="text-end fw-bold">{expenses.length}</td>
                                <td className="text-end fw-bold">100.00%</td>
                              </tr>
                            </tfoot>
                          </table>
                          <TablePagination
                            currentPage={expenseCategoriesPage}
                            totalPages={expenseCategoriesTotalPages}
                            totalItems={expenseCategoriesTotal}
                            startIndex={expenseCategoriesStart}
                            endIndex={expenseCategoriesEnd}
                            pageSize={expenseCategoriesPageSize}
                            onPageChange={setExpenseCategoriesPage}
                            onPageSizeChange={setExpenseCategoriesPageSize}
                            itemLabel="categories"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            )}

            {/* Expenses by Branch */}
            {expenseBranchData.length > 0 && (
                <div className="row g-4 mb-4">
                  <div className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-body">
                        <h5 className="card-title">Expenses by Branch</h5>
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Branch</th>
                                <th className="text-end">Total Amount</th>
                                <th className="text-end">Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedExpenseBranches.map((branch, idx) => {
                                const count = expenses.filter((e) => (e.branch_name || 'No Branch') === branch.name).length
                                return (
                                  <tr key={idx}>
                                    <td className="fw-semibold">{branch.name}</td>
                                    <td className="text-end fw-semibold">{formatCurrency(branch.total)}</td>
                                    <td className="text-end">{count}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                            <tfoot className="table-light">
                              <tr>
                                <td className="fw-bold">Total</td>
                                <td className="text-end fw-bold">
                                  {formatCurrency(expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}
                                </td>
                                <td className="text-end fw-bold">{expenses.length}</td>
                              </tr>
                            </tfoot>
                          </table>
                          <TablePagination
                            currentPage={expenseBranchesPage}
                            totalPages={expenseBranchesTotalPages}
                            totalItems={expenseBranchesTotal}
                            startIndex={expenseBranchesStart}
                            endIndex={expenseBranchesEnd}
                            pageSize={expenseBranchesPageSize}
                            onPageChange={setExpenseBranchesPage}
                            onPageSizeChange={setExpenseBranchesPageSize}
                            itemLabel="branches"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            )}

            {/* Recent Expenses Table */}
            <div className="row g-4 mb-4">
              <div className="col-12">
                <div className="card shadow-sm border-0">
                  <div className="card-body">
                    <h5 className="card-title">Expense Details</h5>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Description</th>
                            <th>Category</th>
                            <th>Branch</th>
                            <th>Warehouse</th>
                            <th className="text-end">Amount</th>
                            <th className="text-end">Date</th>
                            <th>Recorded By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedExpenses.map((expense) => (
                              <tr key={expense.id}>
                                <td>
                                  <div className="fw-semibold text-dark">{expense.description || 'No description'}</div>
                                </td>
                                <td>{expense.category_name || '—'}</td>
                                <td>{expense.branch_name || '—'}</td>
                                <td>{expense.warehouse_name || '—'}</td>
                                <td className="text-end fw-semibold">{formatCurrency(Number(expense.amount) || 0)}</td>
                                <td className="text-end text-muted">
                                  {expense.incurred_on
                                    ? new Date(expense.incurred_on).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })
                                    : '—'}
                                </td>
                                <td>
                                  <small className="text-muted">{expense.created_by_email || 'Unknown'}</small>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td colSpan="4" className="fw-bold">
                              Total ({expenses.length} expenses)
                            </td>
                            <td className="text-end fw-bold">
                              {formatCurrency(expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </table>
                      <TablePagination
                        currentPage={expensesPage}
                        totalPages={expensesTotalPages}
                        totalItems={expensesTableTotal}
                        startIndex={expensesStart}
                        endIndex={expensesEnd}
                        pageSize={expensesPageSize}
                        onPageChange={setExpensesPage}
                        onPageSizeChange={setExpensesPageSize}
                        itemLabel="expenses"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default Reports
